import * as Sentry from "@sentry/react";
import { useState, useEffect, useCallback } from "react";

type ToastProps = {
  title?: string;
  description?: string;
  variant?: "default" | "destructive";
};

type Toast = ToastProps & {
  id: string;
};

// Global toast store - shared across all components
let globalToasts: Toast[] = [];
let listeners: Array<(toasts: Toast[]) => void> = [];
let toastCount = 0;

function notifyListeners() {
  listeners.forEach((listener) => listener([...globalToasts]));
}

function addToast(props: ToastProps): string {
  const id = (toastCount++).toString();
  const newToast: Toast = { id, ...props };
  globalToasts = [...globalToasts, newToast];
  notifyListeners();

  // Report destructive toasts to Sentry as breadcrumbs + events
  if (props.variant === "destructive") {
    const message = [props.title, props.description].filter(Boolean).join(': ');
    Sentry.captureMessage(message || "Unknown error toast", {
      level: "error",
      tags: { source: "error-toast" },
      extra: { title: props.title, description: props.description },
    });
  }

  // Auto-dismiss after 5 seconds
  setTimeout(() => {
    removeToast(id);
  }, 5000);

  return id;
}

function removeToast(toastId: string) {
  globalToasts = globalToasts.filter((t) => t.id !== toastId);
  notifyListeners();
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>(globalToasts);

  useEffect(() => {
    listeners.push(setToasts);
    return () => {
      listeners = listeners.filter((l) => l !== setToasts);
    };
  }, []);

  const toast = useCallback(({ title, description, variant = "default" }: ToastProps) => {
    return addToast({ title, description, variant });
  }, []);

  const dismiss = useCallback((toastId: string) => {
    removeToast(toastId);
  }, []);

  return {
    toast,
    dismiss,
    toasts,
  };
}
